import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { BACKEND_URL } from "@/lib/api";

/**
 * MaintenanceContext
 *
 * Single source of truth for the public maintenance flag.
 * - Polls GET /api/public/maintenance on mount and every 60s.
 * - Listens on the global "kc:maintenance-503" event (dispatched by the api interceptor
 *   when ANY public endpoint returns 503 with {maintenance:true}) and immediately flips
 *   the flag without waiting for the next poll.
 * - Children can call refresh() after a known toggle (e.g. admin saving).
 */
const MaintenanceContext = createContext({
  enabled: false,
  message: null,
  loaded: false,
  refresh: async () => {},
});

const POLL_INTERVAL_MS = 60_000;

export function MaintenanceProvider({ children }) {
  const [state, setState] = useState({ enabled: false, message: null, loaded: false });

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/public/maintenance`, {
        timeout: 5000,
      });
      setState({ enabled: !!data?.enabled, message: data?.message || null, loaded: true });
    } catch {
      // Network/backend down → keep last known state but mark loaded so the app renders.
      setState((s) => ({ ...s, loaded: true }));
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    const handler = (ev) => {
      const msg = ev?.detail?.message || null;
      setState({ enabled: true, message: msg, loaded: true });
    };
    window.addEventListener("kc:maintenance-503", handler);
    return () => {
      clearInterval(id);
      window.removeEventListener("kc:maintenance-503", handler);
    };
  }, [refresh]);

  return (
    <MaintenanceContext.Provider value={{ ...state, refresh }}>
      {children}
    </MaintenanceContext.Provider>
  );
}

export function useMaintenance() {
  return useContext(MaintenanceContext);
}
